import { useState } from 'react'
import { CheckCircle, XCircle, Loader2, Eye, EyeOff } from 'lucide-react'
import { useProxyConfig } from '@/hooks/useProxyConfig'
import { createProfile } from '@/api/profiles'

interface OnboardingWizardProps {
  onComplete: () => void
}

type Step = 'name' | 'url' | 'success'

interface TestState {
  status: 'idle' | 'testing' | 'ok' | 'error'
  error?: string
  latencyMs?: number
}

export function OnboardingWizard({ onComplete }: OnboardingWizardProps) {
  const { proxyUrl, proxyToken } = useProxyConfig()

  const [step, setStep] = useState<Step>('name')
  const [clientName, setClientName] = useState('')
  const [nameError, setNameError] = useState<string | null>(null)

  const [dbUrl, setDbUrl] = useState('')
  const [showUrl, setShowUrl] = useState(false)
  const [testState, setTestState] = useState<TestState>({ status: 'idle' })
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  function handleNameNext() {
    if (clientName.trim().length < 2) {
      setNameError('El nombre debe tener al menos 2 caracteres')
      return
    }
    setNameError(null)
    setStep('url')
  }

  async function handleTestConnection() {
    if (!proxyUrl || !dbUrl.trim()) return
    setTestState({ status: 'testing' })
    const start = Date.now()
    try {
      const r = await fetch(`${proxyUrl}/local/test-connection`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(proxyToken ? { Authorization: `Bearer ${proxyToken}` } : {}),
        },
        body: JSON.stringify({ url: dbUrl }),
        signal: AbortSignal.timeout(6000),
      })
      const data = await r.json() as { success: boolean; error?: string }
      if (data.success) {
        setTestState({ status: 'ok', latencyMs: Date.now() - start })
      } else {
        setTestState({ status: 'error', error: data.error ?? 'Error de conexión' })
      }
    } catch {
      setTestState({ status: 'error', error: 'Sin respuesta del servidor. Verifica que el host sea accesible.' })
    }
  }

  async function handleSave() {
    if (!proxyUrl) return
    setSaving(true)
    setSaveError(null)
    try {
      await createProfile(proxyUrl, proxyToken, clientName.trim(), dbUrl)
      setStep('success')
    } catch (err: any) {
      setSaveError(err.message ?? 'Error al guardar')
    } finally {
      setSaving(false)
    }
  }

  const STEPS = [
    { id: 'name' as Step, num: 1, label: 'Nombre', desc: 'Identifica el cliente' },
    { id: 'url'  as Step, num: 2, label: 'Base de datos', desc: 'Conexión PostgreSQL' },
    { id: 'success' as Step, num: 3, label: 'Listo', desc: 'Configuración completa' },
  ]

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      {/* Indicador horizontal (mobile) */}
      <div className="md:hidden flex items-center gap-2 mb-8 absolute top-8 left-1/2 -translate-x-1/2">
        {STEPS.map((s, i) => (
          <div key={s.id} className="flex items-center gap-2">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
              step === s.id ? 'bg-accent text-background' :
              s.id === 'success' && step === 'success' ? 'bg-green-500 text-white' :
              'bg-border text-text-muted'
            }`}>{s.num}</div>
            {i < 2 && <div className={`w-8 h-px ${step !== 'name' && i === 0 ? 'bg-accent' : 'bg-border'}`} />}
          </div>
        ))}
      </div>

      {/* Layout desktop: sidebar izquierda + contenido derecha */}
      <div className="w-full max-w-2xl hidden md:grid grid-cols-[180px_1fr] gap-8 items-start">

        {/* Sidebar de pasos (desktop) */}
        <div className="flex flex-col gap-1 pt-2">
          <div className="flex items-center gap-2 mb-6 px-3">
            <div className="w-7 h-7 bg-accent rounded-lg flex items-center justify-center shrink-0">
              <span className="text-background font-bold text-xs">IS</span>
            </div>
            <span className="text-text-primary font-bold text-base">ISAPI</span>
          </div>
          {STEPS.map((s, i) => {
            const done = (s.id === 'name' && (step === 'url' || step === 'success')) ||
                         (s.id === 'url'  && step === 'success')
            const active = step === s.id
            return (
              <div key={s.id} className="flex items-start gap-3 px-3 py-2.5 rounded-lg relative">
                {i < STEPS.length - 1 && (
                  <div className={`absolute left-[22px] top-[36px] w-px h-6 ${done ? 'bg-accent/40' : 'bg-border'}`} />
                )}
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5 transition-colors ${
                  done ? 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-700' :
                  active ? 'bg-accent text-background' :
                  'bg-border text-text-muted'
                }`}>
                  {done ? '✓' : s.num}
                </div>
                <div>
                  <p className={`text-xs font-semibold leading-tight ${active ? 'text-text-primary' : 'text-text-muted'}`}>{s.label}</p>
                  <p className="text-[10px] text-text-muted leading-tight mt-0.5">{s.desc}</p>
                </div>
              </div>
            )
          })}
        </div>

        {/* Contenido del paso (desktop) */}
        <div>
          {step === 'name' && (
            <div className="bg-surface border border-border rounded-xl p-8">
              <h2 className="text-text-primary font-semibold text-lg mb-1">Bienvenido a isapi</h2>
              <p className="text-text-muted text-sm mb-6">
                Para comenzar, ingresa el nombre del cliente que vas a configurar.
              </p>
              <div className="flex flex-col gap-1.5 mb-4">
                <label className="text-text-muted text-xs font-medium uppercase tracking-wide">Nombre del cliente</label>
                <input
                  type="text" value={clientName}
                  onChange={e => { setClientName(e.target.value); setNameError(null) }}
                  onKeyDown={e => e.key === 'Enter' && handleNameNext()}
                  placeholder="Ej: Gym Norte" autoFocus
                  className={`bg-background border rounded-md px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-accent transition-colors ${nameError ? 'border-red-500' : 'border-border'}`}
                />
                {nameError && <p className="text-red-500 text-xs">{nameError}</p>}
              </div>
              <button onClick={handleNameNext} className="w-full bg-accent hover:bg-accent-hover text-background font-semibold text-sm py-2.5 rounded-md transition-colors">
                Siguiente →
              </button>
            </div>
          )}
          {step === 'url' && (
            <div className="bg-surface border border-border rounded-xl p-8">
              <h2 className="text-text-primary font-semibold text-lg mb-1">Conexión a la base de datos</h2>
              <p className="text-text-muted text-sm mb-6">
                Ingresa la URL de PostgreSQL para <strong className="text-text-primary">{clientName}</strong>.
              </p>
              <div className="flex flex-col gap-1.5 mb-4">
                <label className="text-text-muted text-xs font-medium uppercase tracking-wide">URL de base de datos</label>
                <div className="relative">
                  <input
                    type={showUrl ? 'text' : 'password'} value={dbUrl}
                    onChange={e => { setDbUrl(e.target.value); setTestState({ status: 'idle' }) }}
                    placeholder="postgresql://user:pass@host:5432/db"
                    className="w-full bg-background border border-border rounded-md px-3 py-2 pr-9 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-accent font-mono"
                  />
                  <button type="button" onClick={() => setShowUrl(v => !v)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary transition-colors">
                    {showUrl ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              {testState.status === 'ok' && (
                <div className="flex items-center gap-2 text-xs px-3 py-2 rounded-lg bg-green-500/10 text-green-600 mb-4">
                  <CheckCircle className="w-3.5 h-3.5 shrink-0" />
                  Conexión exitosa{testState.latencyMs ? ` (${testState.latencyMs}ms)` : ''}
                </div>
              )}
              {testState.status === 'error' && (
                <div className="flex items-center gap-2 text-xs px-3 py-2 rounded-lg bg-red-500/10 text-red-500 mb-4">
                  <XCircle className="w-3.5 h-3.5 shrink-0" />
                  {testState.error}
                </div>
              )}
              {saveError && <p className="text-red-500 text-xs mb-4">{saveError}</p>}
              <div className="flex gap-2 mb-3">
                <button onClick={handleTestConnection} disabled={testState.status === 'testing' || !dbUrl.trim()} className="flex-1 flex items-center justify-center gap-1.5 border border-border text-text-primary hover:bg-background disabled:opacity-50 text-xs font-medium py-2 rounded-md transition-colors">
                  {testState.status === 'testing' && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Probar conexión
                </button>
                <button onClick={handleSave} disabled={saving || !dbUrl.trim()} className="flex-1 flex items-center justify-center gap-1.5 bg-accent hover:bg-accent-hover disabled:opacity-50 text-background text-xs font-semibold py-2 rounded-md transition-colors">
                  {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Guardar y continuar
                </button>
              </div>
              <button onClick={() => setStep('name')} className="w-full text-center text-xs text-text-muted hover:text-text-primary transition-colors py-1">
                ← Atrás
              </button>
            </div>
          )}
          {step === 'success' && (
            <div className="bg-surface border border-border rounded-xl p-8 text-center">
              <div className="flex justify-center mb-4">
                <div className="w-14 h-14 rounded-full bg-green-500/10 flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
              </div>
              <h2 className="text-text-primary font-semibold text-lg mb-2">¡Configuración lista!</h2>
              <p className="text-text-muted text-sm mb-6">
                El perfil <strong className="text-text-primary">{clientName}</strong> fue guardado correctamente.
              </p>
              <button onClick={onComplete} className="w-full bg-accent hover:bg-accent-hover text-background font-semibold text-sm py-2.5 rounded-md transition-colors">
                Iniciar sesión →
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Layout mobile (columna única) */}
      <div className="w-full max-w-sm md:hidden mt-12">
        {step === 'name' && (
          <div className="bg-surface border border-border rounded-xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 bg-accent rounded-lg flex items-center justify-center">
                <span className="text-background font-bold text-sm">IS</span>
              </div>
              <span className="text-text-primary font-bold text-xl">ISAPI</span>
            </div>
            <h2 className="text-text-primary font-semibold text-lg mb-1">Bienvenido a isapi</h2>
            <p className="text-text-muted text-sm mb-6">Para comenzar, ingresa el nombre del cliente que vas a configurar.</p>
            <div className="flex flex-col gap-1.5 mb-4">
              <label className="text-text-muted text-xs font-medium uppercase tracking-wide">Nombre del cliente</label>
              <input
                type="text" value={clientName}
                onChange={e => { setClientName(e.target.value); setNameError(null) }}
                onKeyDown={e => e.key === 'Enter' && handleNameNext()}
                placeholder="Ej: Gym Norte" autoFocus
                className={`bg-background border rounded-md px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-accent transition-colors ${nameError ? 'border-red-500' : 'border-border'}`}
              />
              {nameError && <p className="text-red-500 text-xs">{nameError}</p>}
            </div>
            <button onClick={handleNameNext} className="w-full bg-accent hover:bg-accent-hover text-background font-semibold text-sm py-2.5 rounded-md transition-colors">Siguiente →</button>
          </div>
        )}
        {step === 'url' && (
          <div className="bg-surface border border-border rounded-xl p-8">
            <h2 className="text-text-primary font-semibold text-lg mb-1">Conexión a la base de datos</h2>
            <p className="text-text-muted text-sm mb-6">Ingresa la URL de PostgreSQL para <strong className="text-text-primary">{clientName}</strong>.</p>
            <div className="flex flex-col gap-1.5 mb-4">
              <label className="text-text-muted text-xs font-medium uppercase tracking-wide">URL de base de datos</label>
              <div className="relative">
                <input
                  type={showUrl ? 'text' : 'password'} value={dbUrl}
                  onChange={e => { setDbUrl(e.target.value); setTestState({ status: 'idle' }) }}
                  placeholder="postgresql://user:pass@host:5432/db"
                  className="w-full bg-background border border-border rounded-md px-3 py-2 pr-9 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-accent font-mono"
                />
                <button type="button" onClick={() => setShowUrl(v => !v)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary transition-colors">
                  {showUrl ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            {testState.status === 'ok' && <div className="flex items-center gap-2 text-xs px-3 py-2 rounded-lg bg-green-500/10 text-green-600 mb-4"><CheckCircle className="w-3.5 h-3.5 shrink-0" />Conexión exitosa{testState.latencyMs ? ` (${testState.latencyMs}ms)` : ''}</div>}
            {testState.status === 'error' && <div className="flex items-center gap-2 text-xs px-3 py-2 rounded-lg bg-red-500/10 text-red-500 mb-4"><XCircle className="w-3.5 h-3.5 shrink-0" />{testState.error}</div>}
            {saveError && <p className="text-red-500 text-xs mb-4">{saveError}</p>}
            <div className="flex gap-2 mb-3">
              <button onClick={handleTestConnection} disabled={testState.status === 'testing' || !dbUrl.trim()} className="flex-1 flex items-center justify-center gap-1.5 border border-border text-text-primary hover:bg-background disabled:opacity-50 text-xs font-medium py-2 rounded-md transition-colors">
                {testState.status === 'testing' && <Loader2 className="w-3.5 h-3.5 animate-spin" />}Probar conexión
              </button>
              <button onClick={handleSave} disabled={saving || !dbUrl.trim()} className="flex-1 flex items-center justify-center gap-1.5 bg-accent hover:bg-accent-hover disabled:opacity-50 text-background text-xs font-semibold py-2 rounded-md transition-colors">
                {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}Guardar y continuar
              </button>
            </div>
            <button onClick={() => setStep('name')} className="w-full text-center text-xs text-text-muted hover:text-text-primary transition-colors py-1">← Atrás</button>
          </div>
        )}
        {step === 'success' && (
          <div className="bg-surface border border-border rounded-xl p-8 text-center">
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 rounded-full bg-green-500/10 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </div>
            <h2 className="text-text-primary font-semibold text-lg mb-2">¡Configuración lista!</h2>
            <p className="text-text-muted text-sm mb-6">El perfil <strong className="text-text-primary">{clientName}</strong> fue guardado correctamente.</p>
            <button onClick={onComplete} className="w-full bg-accent hover:bg-accent-hover text-background font-semibold text-sm py-2.5 rounded-md transition-colors">Iniciar sesión →</button>
          </div>
        )}
      </div>
    </div>
  )
}
